import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Newspaper, Bell, Activity, Radio, Filter, Menu, X } from "lucide-react";
import Ticker from "./Ticker";
import StockSearch from "./StockSearch";
import { useApiStore } from "@/store/useApiStore";
import { isTradingDay } from "@/lib/trading";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "总览", icon: LayoutDashboard, end: true },
  { to: "/subscription", label: "订阅中心", icon: Bell, end: false },
  { to: "/feed", label: "我的情报流", icon: Newspaper, end: false },
  { to: "/picker", label: "选股", icon: Filter, end: false },
];

function Clock() {
  const [now, setNow] = useState(new Date());
  const health = useApiStore((s) => s.health);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const trading = isTradingDay(now);
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  return (
    <div className="space-y-1">
      <div className="num text-2xl text-gold leading-none text-gold-glow">
        {hh}:{mm}
        <span className="text-sm text-ink-500">:{ss}</span>
      </div>
      <div className="flex items-center gap-1.5 text-[0.6875rem]">
        <span
          className={cn(
            "inline-block w-1.5 h-1.5 rounded-full pulse-dot",
            trading ? "bg-bull" : "bg-ink-500",
          )}
        />
        <span className={trading ? "text-bull" : "text-ink-400"}>
          {trading ? "交易日 · 实时推送中" : "非交易日 · 定时推送"}
        </span>
      </div>
    </div>
  );
}

export default function Layout() {
  const loc = useLocation();
  const { health, refresh, initialized } = useApiStore();

  // 挂载时拉一次共享状态（订阅/历史/健康）
  useEffect(() => {
    if (!initialized) refresh();
  }, [initialized, refresh]);

  const subCount = useApiStore((s) => s.subscriptions.filter((x) => x.enabled).length);
  // 移动端抽屉导航开合
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <Ticker />

      {/* 移动端抽屉导航（汉堡菜单） */}
      {drawerOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setDrawerOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 z-50 w-64 max-w-[80vw] bg-ink-950 border-r border-ink-700 flex flex-col md:hidden">
            <div className="p-4 border-b border-ink-700 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 border border-gold flex items-center justify-center bg-gold/10">
                  <Activity size={18} className="text-gold" />
                </div>
                <div>
                  <div className="font-serif text-lg text-ink-100 leading-none">股讯</div>
                  <div className="text-[0.625rem] font-mono text-ink-500 tracking-widest mt-0.5">GUXUN TERMINAL</div>
                </div>
              </div>
              <button onClick={() => setDrawerOpen(false)} aria-label="关闭菜单" className="p-1 text-ink-400 hover:text-ink-100">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 border-b border-ink-700">
              <Clock />
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-auto">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-3 text-sm transition-colors border-l-2",
                      isActive
                        ? "border-gold bg-gold/5 text-gold"
                        : "border-transparent text-ink-300 hover:text-ink-100 hover:bg-ink-800/60",
                    )
                  }
                >
                  <n.icon size={16} />
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="p-4 border-t border-ink-700">
              <div className="flex items-center gap-2 text-[0.6875rem] text-ink-400">
                <Radio size={12} className={health ? "text-gold" : "text-ink-600"} />
                <span>{health ? `${subCount} 只股票订阅中` : "连接后端中…"}</span>
              </div>
            </div>
          </aside>
        </>
      )}
      <div className="flex flex-1 min-h-0">
        <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-ink-700 bg-ink-900/40">
          <div className="p-5 border-b border-ink-700">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 border border-gold flex items-center justify-center bg-gold/10 animate-glowPulse">
                <Activity size={18} className="text-gold" />
              </div>
              <div>
                <div className="font-serif text-lg text-ink-100 leading-none">股讯</div>
                <div className="text-[0.625rem] font-mono text-ink-500 tracking-widest mt-0.5">
                  GUXUN TERMINAL
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border-b border-ink-700">
            <Clock />
          </div>

          <nav className="flex-1 p-3 space-y-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 text-sm transition-colors border-l-2",
                    isActive
                      ? "border-gold bg-gold/5 text-gold"
                      : "border-transparent text-ink-300 hover:text-ink-100 hover:bg-ink-800/60",
                  )
                }
              >
                <n.icon size={16} />
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* 真实健康状态 */}
          <div className="p-4 border-t border-ink-700 space-y-2">
            <div className="flex items-center gap-2 text-[0.6875rem] text-ink-400">
              <Radio size={12} className={health ? "text-gold" : "text-ink-600"} />
              <span>
                {health
                  ? `${subCount} 只股票订阅中`
                  : "连接后端中…"}
              </span>
            </div>
            {health && (
              <div className="text-[0.625rem] font-mono text-ink-500 leading-relaxed space-y-0.5">
                <div>存储 · {health.storageMode}</div>
                <div className="flex items-center gap-1">
                  DeepSeek ·
                  <span className={health.deepseekEnabled ? "text-bull" : "text-ink-600"}>
                    {health.deepseekEnabled ? "已启用" : "未启用"}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  推送模式 ·
                  <span className="text-gold">{health.mergePushDefault ? "合并" : "逐股"}</span>
                </div>
              </div>
            )}
          </div>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          <header className="sticky top-0 z-20 border-b border-ink-700 bg-ink-950/80 backdrop-blur px-4 md:px-6 py-3 flex items-center gap-4">
            <div className="md:hidden flex items-center gap-2">
              <button
                onClick={() => setDrawerOpen(true)}
                aria-label="打开菜单"
                className="p-1 -ml-1 text-gold"
              >
                <Menu size={20} />
              </button>
              <Activity size={18} className="text-gold" />
              <span className="font-serif text-base text-ink-100">股讯</span>
            </div>
            <div className="hidden md:block">
              <div className="text-[0.6875rem] font-mono text-ink-500 tracking-widest uppercase">
                {loc.pathname === "/" ? "Overview" : loc.pathname.split("/")[1]}
              </div>
            </div>
            <div className="flex-1 max-w-xl ml-auto">
              <StockSearch size="sm" />
            </div>
          </header>
          <main className="flex-1 min-h-0 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
